// 問題回報（企業客戶支援，plan SPEC #support-business，Eddy 2026-10-21 選單）：組織的 IT / 管理者從 VMS 回報問題。
//   - 誰能回報：team 組織的 owner / org_admin，或有組織權限 org.support 的成員（hasOrgPerm）；入口只對他們顯示。
//   - 單記在 audience = business、品牌 = 組織品牌、org_id = 開單組織（建單後不能改）；同組織有回報權的人看得到、能留言。
//   - 一律附 context.json（VMS 版本、瀏覽器、頁面、組織、設備、最近 API 錯誤），表單可以展開看全文。
// API 在組織所在 plane 的 app-api：/v1/support/tickets?audience=business。
import { kinioFetch, planeBase, setDiagnosticLogger } from '@kinio/web-sdk';
import { hasOrgPerm, type MyOrg } from './planeApi';

export type SupportScope = 'all' | 'mine' | 'watching' | 'org';
export const SUPPORT_SCOPES: SupportScope[] = ['all', 'mine', 'watching', 'org'];
/** 分類（SPEC #support-business 表單）：影像與串流 / 設備離線 / 錄影與回放 / 帳號與權限 / 通知與警報 / 其他。 */
export const SUPPORT_CATEGORIES = ['video', 'offline', 'recording', 'account', 'alert', 'other'] as const;
/** 平台附件白名單（SPEC #support-dev-app 附件限制）；一張最多 8 個，表單讓使用者加 5 個（context.json 另佔一個）。 */
export const SUPPORT_ATTACH_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'text/plain', 'application/json', 'application/zip', 'application/pdf'];
export const SUPPORT_MAX_FILES = 5;
export const SUPPORT_MAX_BYTES = 20 << 20;

export interface SupportTicket {
  ticket_id: string;
  brand: string;
  user_id: string;
  org_id?: string;
  org_name?: string;
  audience: string;
  category: string;
  subject: string;
  body: string;
  device_ref?: string;
  device_model?: string;
  status: 'open' | 'triaged' | 'in_progress' | 'resolved' | 'closed' | string;
  created_at: string;
  updated_at: string;
  unread?: boolean;
  relation?: 'owner' | 'watcher' | 'org';
  owner_name?: string;
  can_manage?: boolean;
  events?: SupportEvent[];
  attachments?: { attachment_id: string; filename: string; content_type: string; size_bytes: number; created_at: string }[];
  watchers?: { user_id: string; name?: string; email?: string }[];
}

export interface SupportEvent {
  event_id: number;
  kind: string;
  actor: string;
  actor_role: string;
  actor_name?: string;
  body?: string;
  created_at: string;
}

/** 回報落在哪個 plane（組織所在的 app-api）。 */
export interface SupportPlane {
  api: string;
}

/** 我有回報權的組織：team、沒有停用提示、owner / org_admin 或 org.support。 */
export function reporterOrgs(orgs: MyOrg[]): MyOrg[] {
  return orgs.filter((o) => o.kind === 'team' && !o.notice && hasOrgPerm(o, 'org.support'));
}

/** 有回報權的組織所在的 plane（去重、照組織順序）。 */
export function reporterApis(orgs: MyOrg[]): string[] {
  return [...new Set(reporterOrgs(orgs).map((o) => o.api))];
}

const base = (api: string) => `${planeBase(api)}/v1/support/tickets`;

export const supportApi = {
  list: (api: string, scope: SupportScope = 'all') =>
    kinioFetch<{ tickets: SupportTicket[] }>(`${base(api)}?audience=business&scope=${scope}`).then((r) => r?.tickets ?? []),
  get: (api: string, id: string) => kinioFetch<SupportTicket>(`${base(api)}/${encodeURIComponent(id)}`),
  create: (
    api: string,
    body: { org_id: string; category: string; subject: string; body: string; device_ref?: string; device_model?: string; language?: string; app_version?: string; override_reason?: string },
  ) => kinioFetch<SupportTicket & { review?: { summary?: string } }>(base(api), { method: 'POST', body: { audience: 'business', app_id: 'vms-web', ...body } }),
  comment: (api: string, id: string, body: string) => kinioFetch<unknown>(`${base(api)}/${encodeURIComponent(id)}/comments`, { method: 'POST', body: { body } }),
  attach: (api: string, id: string, file: Blob, name: string) => {
    const f = new FormData();
    f.append('file', file, name);
    return kinioFetch<unknown>(`${base(api)}/${encodeURIComponent(id)}/attachments`, { method: 'POST', body: f });
  },
  unread: (api: string) => kinioFetch<{ count: number }>(`${planeBase(api)}/v1/support/unread?audience=business`).then((r) => r?.count ?? 0),
};

// ---- 最近的 API 錯誤（context.json 用）：SDK 的診斷記錄只給方法、路徑（不含 query）、狀態與錯誤碼 ----

const MAX_ERRORS = 20;
const apiErrors: { at: string; line: string }[] = [];

/** 開機時呼叫一次：把 SDK 的 API 錯誤留最近 20 筆（只在記憶體）。 */
export function installApiErrorLog(): void {
  setDiagnosticLogger((_level, message) => {
    apiErrors.push({ at: new Date().toISOString(), line: message });
    if (apiErrors.length > MAX_ERRORS) apiErrors.splice(0, apiErrors.length - MAX_ERRORS);
  });
}

export function recentApiErrors(): { at: string; line: string }[] {
  return [...apiErrors];
}

export interface SupportContextInput {
  version: string;
  build: string;
  org: Pick<MyOrg, 'org_id' | 'display_name'>;
  device?: { aid: string; name?: string; model?: string; firmware?: string; online?: boolean; path?: string } | null;
  page: string;
  language: string;
}

/** context.json 的內容（SPEC #support-business「自動帶的證據」）；頁面網址去掉 query。 */
export function supportContext(i: SupportContextInput, now = new Date()): Record<string, unknown> {
  const q = i.page.indexOf('?');
  return {
    app: 'vms-web',
    vms_version: i.version || 'dev',
    vms_build: i.build,
    browser: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    language: i.language,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: q < 0 ? i.page : i.page.slice(0, q),
    org: { org_id: i.org.org_id, name: i.org.display_name },
    device: i.device ?? null,
    recent_api_errors: recentApiErrors(),
    submitted_at: now.toISOString(),
  };
}

/** context.json 序列化（斜線不跳脫，時區 Asia/Taipei 照原樣）。 */
export function contextJson(c: Record<string, unknown>): string {
  return JSON.stringify(c, null, 2);
}
